import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, getToken, setToken, clearToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password, remember = false, turnstileToken = null) => {
    try {
      const { data } = await api.post("/auth/login", {
        email,
        password,
        remember,
        ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
      });
      setToken(data.token, remember);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Compte bloqué : le backend retourne un support_token temporaire
      // permettant de créer un ticket sans authentification normale.
      if (typeof detail === "object" && detail?.code === "ACCOUNT_BLOCKED") {
        return {
          ok: false,
          blocked: true,
          support_token: detail.support_token,
          user_email: detail.user_email,
          user_phone: detail.user_phone,
          user_name: detail.user_name,
        };
      }
      return { ok: false, error: formatApiError(detail) };
    }
  };

  const register = async (name, email, password, phone) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password, phone });
      setToken(data.token, false);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const logout = () => {
    clearToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
